import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion, type Variants } from "motion/react";
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";

import { cn } from "../utils";

const motionIcon: Variants = {
  hidden: {
    transform: "rotateZ(0deg)",
    transition: { duration: 0.6, ease: "easeOut" },
  },
  visible: {
    transform: "rotateZ(180deg)",
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const AccordionItemIcon = ({ isOpen }: { isOpen: boolean }) => (
  <motion.div
    className={cn("relative mt-0 size-6 shrink-0")}
    variants={motionIcon}
    initial="hidden"
    animate={isOpen ? "visible" : "hidden"}
  >
    <ChevronDown
      strokeWidth={3}
      className="absolute h-auto w-full text-primary"
    />
  </motion.div>
);

export const AccordionItem = ({
  title,
  children,
  id,
  className,
  titleStyling,
  accordionHeaderStyling,
  bodyStyling,
  defaultExpanded = false,
  expanded: controlledExpanded,
  onExpandedChange,
  separatorWhenOpen = false,
}: {
  title: string | ReactNode;
  children: ReactNode;
  id?: string;
  className?: string;
  titleStyling?: string;
  accordionHeaderStyling?: string;
  bodyStyling?: string;
  defaultExpanded?: boolean;
  /** Controlled open state; omit to self-manage. Still pass `defaultExpanded`
   *  — the focus effect reads it. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  /** When true, show a divider under the header while the accordion is open. */
  separatorWhenOpen?: boolean;
}) => {
  const [uncontrolledExpanded, setUncontrolledExpanded] =
    useState(defaultExpanded);
  const expanded = controlledExpanded ?? uncontrolledExpanded;
  // Fall back to a stable generated id so omitting `id` doesn't produce
  // duplicate `undefined` ids when several accordions render on one page.
  const reactId = useId();
  const resolvedId = id ?? reactId;
  const refContainer = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (expanded && !defaultExpanded) {
      refContainer.current?.focus();
    }
  }, [expanded, defaultExpanded]);

  return (
    <section
      className={cn("relative overflow-hidden rounded-xl border", className)}
    >
      <button
        type="button"
        id={`accordion-${resolvedId}-label`}
        className={cn(
          "m-0 w-full cursor-pointer flex items-center justify-between p-4 md:px-6 md:py-4 transition-colors hover:bg-gray-100 border-0 bg-transparent text-left",
          accordionHeaderStyling,
        )}
        aria-controls={`sect-${resolvedId}`}
        aria-expanded={expanded}
        onClick={() => {
          const next = !expanded;
          setUncontrolledExpanded(next);
          onExpandedChange?.(next);
        }}
      >
        <p className={cn("pb-0 m-0 pr-4 text-base-strong", titleStyling)}>
          {title}
        </p>
        <AccordionItemIcon isOpen={expanded} />
      </button>

      {separatorWhenOpen && expanded && (
        <div className="border-b border-gray-200" />
      )}

      <AnimatePresence>
        <motion.div
          ref={refContainer as RefObject<HTMLDivElement>}
          id={`sect-${resolvedId}`}
          role="region"
          aria-labelledby={`accordion-${resolvedId}-label`}
          tabIndex={-1}
          className={cn("overflow-hidden")}
          initial={false}
          animate={expanded ? "open" : "collapsed"}
          variants={{
            open: { opacity: 1, height: "auto" },
            collapsed: { opacity: 0, height: 0 },
          }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <motion.div
            className={cn("px-4 py-3 md:px-6 md:pb-7 md:pt-3", bodyStyling)}
            animate={expanded ? "open" : "collapsed"}
            variants={{
              open: { visibility: "visible" as const },
              collapsed: {
                visibility: "hidden" as const,
                transition: { delay: 0.6 },
              },
            }}
          >
            {children}
          </motion.div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
};
